import {
  CylinderGeometry,
  Group,
  TorusGeometry,
  Mesh,
  MeshStandardMaterial,
  type BufferGeometry,
  type Material,
} from 'three';
import type { AttackObject } from '../../core/boss/attacks';
import { BOSS_OBJECTS, type BossObjectType } from '../../data/boss-objects';
import { buildHippo, PLUSH_COLORS, type Hippo } from '../models/hippo';
import { shapeToGeometryKind } from './boss-pose';

/**
 * Pool of the attack objects of a boss (GDD §9.2, §13.6, issue #96). The geometry comes from the `shape` of the
 * core: silver coins are shiny flat discs, leads are elongated dark bullets, so they are told apart by form and not
 * only by colour. The plush hippo is the silhouette of the EN-11 in toy felt. A fixed pool is reused every frame
 * (nothing is created while the fight runs) and disposed when the scene leaves the arena.
 */
export const ATTACK_COLORS = { coin: 0xe8eef2, coinGlow: 0x6d7a85, lead: 0x2b2e33, barrel: 0x8a5a3a } as const;
// The horseshoes of JEFE-2 get a plain ring until its model (#111); the portrait walls, the chess pieces and the check columns of JEFE-3 have no model yet (pool of 0, #122).
const POOL: Readonly<Record<BossObjectType, number>> = { 'silver-coin': 8, lead: 8, 'plush-hippo': 4, horseshoe: 6, 'portrait-wall': 0, 'chess-piece': 0, 'check-column': 0, barrel: 6 };
const EMPTY_SLOTS = (): Record<BossObjectType, Group[]> => ({ 'silver-coin': [], lead: [], 'plush-hippo': [], horseshoe: [], 'portrait-wall': [], 'chess-piece': [], 'check-column': [], barrel: [] });
const NO_USE = (): Record<BossObjectType, number> => ({ 'silver-coin': 0, lead: 0, 'plush-hippo': 0, horseshoe: 0, 'portrait-wall': 0, 'chess-piece': 0, 'check-column': 0, barrel: 0 });

export interface AttackPool {
  group: Group;
  /** Shows the objects of the core where they are and hides the unused ones of the pool. */
  update(objects: readonly AttackObject[]): void;
  dispose(): void;
}

export function buildAttackPool(): AttackPool {
  const geometries: BufferGeometry[] = [];
  const materials: Material[] = [];
  const hippos: Hippo[] = [];
  const group = new Group();
  group.name = 'boss-attacks';
  const slots = EMPTY_SLOTS();

  const make = (type: BossObjectType): Group => {
    const def = BOSS_OBJECTS[type];
    const kind = shapeToGeometryKind(def.shape);
    const holder = new Group();
    holder.name = `attack-${type}`;
    if (kind === 'plush') {
      const toy = buildHippo(PLUSH_COLORS, `attack-${type}-model`);
      hippos.push(toy);
      toy.body.visible = true;
      toy.body.scale.setScalar(0.5);
      holder.add(toy.body);
    } else {
      // The disc is a flat cylinder facing the camera; the bullet is a tall thin cylinder (aspect 2.5); the barrel of El Chato
      // (JEFE-4, #134) is a fat cylinder in wood, about the 0.9 u of its box.
      const geometry =
        kind === 'disc'
          ? new CylinderGeometry(def.pickupRadius! * 0.55, def.pickupRadius! * 0.55, 0.08, 14)
          : kind === 'horseshoe'
            ? new TorusGeometry(0.26, 0.07, 5, 8, Math.PI * 1.6)
            : kind === 'barrel'
              ? new CylinderGeometry(0.4, 0.4, 0.7, 8)
              : new CylinderGeometry(0.1, 0.1, 0.5, 6);
      geometries.push(geometry);
      const material =
        kind === 'disc'
          ? new MeshStandardMaterial({ color: ATTACK_COLORS.coin, metalness: 0.7, roughness: 0.3, emissive: ATTACK_COLORS.coinGlow, flatShading: true })
          : new MeshStandardMaterial({ color: kind === 'barrel' ? ATTACK_COLORS.barrel : ATTACK_COLORS.lead, metalness: 0.2, roughness: 0.8, flatShading: true });
      materials.push(material);
      const mesh = new Mesh(geometry, material);
      if (kind === 'disc') mesh.rotation.x = Math.PI / 2;
      holder.add(mesh);
    }
    holder.visible = false;
    group.add(holder);
    return holder;
  };
  for (const type of Object.keys(POOL) as BossObjectType[]) {
    for (let i = 0; i < POOL[type]; i++) slots[type].push(make(type));
  }

  return {
    group,
    update(objects) {
      const used = NO_USE();
      for (const object of objects) {
        const holder = slots[object.type][used[object.type]++];
        if (!holder) continue;
        holder.visible = true;
        holder.position.set(object.x, object.y, 0);
      }
      for (const type of Object.keys(POOL) as BossObjectType[]) {
        for (let i = used[type]; i < slots[type].length; i++) slots[type][i]!.visible = false;
      }
    },
    dispose() {
      for (const toy of hippos) toy.dispose();
      for (const geometry of geometries) geometry.dispose();
      for (const material of materials) material.dispose();
    },
  };
}
